/**
 * Student Lecture Service
 * Handles fetching scheduled lectures for the logged-in student
 */

import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, catchError, map, throwError } from 'rxjs';
import { PORTAL_STUDENT_TIMETABLE_ENDPOINTS } from '../api';

export interface StudentLectureTeacher {
  id: number;
  name: string;
  role?: string | null;
}

export interface StudentLectureResource {
  id: number;
  code?: string | null;
  name?: string | null;
}

export interface StudentLecture {
  lecture_block_id: number;
  fk_school_id: number;
  school_shortname?: string | null;
  module_run_id: number;
  module_run_name?: string | null;
  teachers: StudentLectureTeacher[];
  resources: StudentLectureResource[];
  reservation_date: string | null;
  reservation_time_from: string | null;
  reservation_time_to: string | null;
  module_id: number;
  module_name: string;
  degree_id?: number | null;
  degree_name?: string | null;
  degree_code?: string | null;
  location_id?: number | null;
  location_name?: string | null;
  location_shortname?: string | null;
  module_variant_id?: number | null;
  variant_code?: string | null;
  variant_name?: string | null;
  cohort_start_date?: string | null;
  nbr_of_lectures?: number | null;
  internal_comments?: string | null;
  infoscreen_comments?: string | null;
  fk_status_id?: number | null;
  is_backup?: boolean | null;
  is_reserve?: boolean | null;
  planning_status?: string | null;
}

export interface StudentLecturesResponse {
  results: StudentLecture[];
  total_count: number;
}

@Injectable({
  providedIn: 'root',
})
export class StudentLectureService {
  private readonly http = inject(HttpClient);

  private createHeaders(): HttpHeaders {
    const token = localStorage.getItem('access_token') || localStorage.getItem('accessToken');
    return new HttpHeaders({
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    });
  }

  getStudentLectures(contactId: number, dateFrom?: string, dateTo?: string): Observable<StudentLecture[]> {
    let url = PORTAL_STUDENT_TIMETABLE_ENDPOINTS.lectures(contactId);
    if (dateFrom) url += `&date_from=${dateFrom}`;
    if (dateTo) url += `&date_to=${dateTo}`;

    return this.http
      .get<StudentLecturesResponse>(url, { headers: this.createHeaders() })
      .pipe(
        map((res) => res.results || []),
        catchError((error) => {
          console.error('❌ Error loading student lectures:', error);
          return throwError(() => error);
        })
      );
  }
}
